import React, { useState, type JSX } from "react";
import { CardContainer, OuterCardContainer } from "../../components/Card/CardContainer";
import { Col, Row, Tabs } from "antd";
import Text from "../../components/Text/Text";
import HistoryPage from "./TabSection/HistoryPage";
import AchievePage from "./TabSection/AchievePage";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Button from "../../components/Button/Button";
import { CheckCircleOutlined, EditOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DetailPage from "./TabSection/DetailPage";
import { ModalConfirm, ModalConfirmType } from "../../components/Modal/Confirm";
import { approveAchieve, getAchievementById } from "../../datasource/AchievementDataSource";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../store/RoleAtom";
import { checkPermission } from "../../components/Permission/Permission";
import color from "../../resource/color";

function DetailAchievement() {
  type TabKey = "DETAIL" | "ACHIEVE" | "HISTORY";
  const [updateStatus, setUpdateStatus] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const initialStatus = location.state?.status;
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { firstname, lastname } = userProfile;
  const [loading, setLoading] = useState(false);
  const [isTabs, setIsTabs] = useState<TabKey>(() => {
    const saved = localStorage.getItem("tabDetailTour") as TabKey | null;
    return saved ?? "DETAIL";
  });
  const roleData = useRecoilValue(roleAtom);
  const isApproved = checkPermission(["achievement", "approve"], roleData);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      await getAchievementById(id!);
    } catch (error) {
      console.error("Error fetching achievement", error);
    } finally {
      setLoading(false);
    }
  };

  const PageTitle = () => (
    <PageTitleNested
      title={"รายละเอียดความสำเร็จ"}
      showBack
      onBack={() => navigate("/crm/achievement")}
      customBreadCrumb={
        <BreadCrumb
          data={[
            { text: "รายการความสำเร็จ", path: "/crm/achievement" },
            { text: "รายละเอียดความสำเร็จ", path: window.location.pathname },
          ]}
        />
      }
      extra={
        <Row gutter={[16, 16]}>
          {initialStatus === "WAIT_APPROVE" && isApproved && (
            <Col>
              <Button
                title='อนุมัติ'
                typeButton='approve'
                icon={<CheckCircleOutlined style={{ color: color.white }} />}
                onClick={() => setUpdateStatus(true)}
              />
            </Col>
          )}
          {initialStatus !== "EXPIRE" && (
            <Col>
              <Button
                title='แก้ไข'
                typeButton='border-primary'
                icon={<EditOutlined />}
                onClick={() => navigate(`/crm/achievement/create/${id}/detail`)}
              />
            </Col>
          )}
        </Row>
      }
    />
  );

  const tabsData: { key: TabKey; label: React.ReactNode; children?: JSX.Element }[] = [
    {
      key: "DETAIL",
      label: (
        <Text fontSize={14} fontWeight={400}>
          รายละเอียด
        </Text>
      ),
      children: <DetailPage isApprove={loading} achievementId={id!} isStep4={false} />,
    },
    {
      key: "ACHIEVE",
      label: (
        <Text fontSize={14} fontWeight={400}>
          ติดตามความสำเร็จ
        </Text>
      ),
      children: <AchievePage />,
    },
    {
      key: "HISTORY",
      label: (
        <Text fontSize={14} fontWeight={400}>
          ประวัติทำรายการ
        </Text>
      ),
      children: <HistoryPage achievementId={id!} />,
    },
  ];

  const handleTabChange = (key: string) => {
    localStorage.setItem("tabDetailTour", key);
    setIsTabs(key as TabKey);
  };

  return (
    <OuterCardContainer>
      <CardContainer cutBottom>
        <PageTitle />
        <Tabs items={tabsData} onChange={handleTabChange} activeKey={isTabs} />
      </CardContainer>

      <ModalConfirm
        title='ต้องการอนุมัติความสำเร็จหรือไม่??'
        description='กรุณากด “ยกเลิก” ถ้าท่านไม่ต้องการอนุมัติ หรือ กด “ยืนยัน” ถ้าท่านต้องการอนุมัติความสำเร็จนี้'
        open={updateStatus}
        toggleOpen={() => setUpdateStatus(false)}
        type={ModalConfirmType.CF}
        onOk={async () => {
          try {
            await approveAchieve({
              achievementId: id!,
              isApproved: true,
              updatedBy: `${firstname} ${lastname}`,
            });
            navigate(location.pathname, { replace: true, state: { status: undefined } });
            await fetchStatus();
            setUpdateStatus(false);
          } catch (error) {
            console.log("อนุมัติไม่สำเร็จ", error);
          }
        }}
      />
    </OuterCardContainer>
  );
}

export default DetailAchievement;
